import type {
  ExtractFileDataInput,
  ExtractFileDataOutput,
  FileExtractionResult,
  FileFormat,
  DocumentTypeConfig,
  ExtractionError,
  ExtractionWarning,
  ParsedFileData,
} from "./types";
import { FILE_EXTENSION_TO_FORMAT } from "./constants";
import { FileExtractionError } from "./errors";
import { getDocumentTypeConfig, isFormatSupported } from "./registry";
import { parseXlsxFile, parsePdfFile } from "./parsers";
import { validateVersion } from "./versionValidator";
import { extractFieldsFromFile } from "./extractor";
import { transformExtracted } from "./transformers";
import { mergeFileResults } from "./merger";

export async function extractDataFromFiles(
  input: ExtractFileDataInput
): Promise<ExtractFileDataOutput> {
  const config = getDocumentTypeConfig(input.documentType);
  if (!config) {
    return {
      fileResults: [],
      mergedData: {
        entityData: {},
        contactData: {},
        impactAnalysisData: {},
        notificationDecision: null,
        riskAnalysis: null,
        romanianStateRelationship: null,
        euServiceProvision: null,
        article9: null,
        evaluarePreliminaraData: null,
      },
      isValid: false,
      errors: [
        {
          file: "",
          code: "UNKNOWN_DOCUMENT_TYPE",
          message: `Unknown document type: "${input.documentType}"`,
        },
      ],
      warnings: [],
    };
  }

  const fileResults = await Promise.all(
    input.files.map((file) => processOneFile(file, config))
  );

  const mergedData = mergeFileResults(fileResults);
  const allErrors = fileResults.flatMap((r) => r.errors);
  const allWarnings = fileResults.flatMap((r) => r.warnings);
  const isValid = fileResults.every((r) => r.isValid);

  return {
    fileResults,
    mergedData,
    isValid,
    errors: allErrors,
    warnings: allWarnings,
  };
}

async function processOneFile(
  file: File,
  config: DocumentTypeConfig
): Promise<FileExtractionResult> {
  const errors: ExtractionError[] = [];
  const warnings: ExtractionWarning[] = [];

  const fileFormat = detectFileFormat(file.name);
  if (!fileFormat) {
    return {
      fileName: file.name,
      fileFormat: "xlsx",
      documentType: config.documentType,
      version: null,
      isValid: false,
      extractedData: null,
      transformedData: null,
      errors: [
        {
          file: file.name,
          code: "UNSUPPORTED_FORMAT",
          message: `Unsupported file extension: "${file.name}"`,
        },
      ],
      warnings: [],
    };
  }

  if (!isFormatSupported(config.documentType, fileFormat)) {
    return {
      fileName: file.name,
      fileFormat,
      documentType: config.documentType,
      version: null,
      isValid: false,
      extractedData: null,
      transformedData: null,
      errors: [
        {
          file: file.name,
          code: "UNSUPPORTED_FORMAT",
          message: `Format "${fileFormat}" is not supported for document type "${config.documentType}"`,
        },
      ],
      warnings: [],
    };
  }

  let parsedFile: ParsedFileData;
  try {
    parsedFile =
      fileFormat === "xlsx"
        ? await parseXlsxFile(file)
        : await parsePdfFile(file);
  } catch (err) {
    const message =
      err instanceof FileExtractionError
        ? err.message
        : `Failed to parse file: ${file.name}`;
    return {
      fileName: file.name,
      fileFormat,
      documentType: config.documentType,
      version: null,
      isValid: false,
      extractedData: null,
      transformedData: null,
      errors: [{ file: file.name, code: "PARSE_FAILED", message }],
      warnings: [],
    };
  }

  const versionResult = validateVersion(parsedFile, config);
  if (versionResult.error) errors.push(versionResult.error);
  if (versionResult.warning) warnings.push(versionResult.warning);

  if (!versionResult.isValid) {
    return {
      fileName: file.name,
      fileFormat,
      documentType: config.documentType,
      version: versionResult.version,
      isValid: false,
      extractedData: null,
      transformedData: null,
      errors,
      warnings,
    };
  }

  const extractionResult = extractFieldsFromFile(parsedFile, config);
  extractionResult.extracted.version = versionResult.version;
  errors.push(...extractionResult.errors);
  warnings.push(...extractionResult.warnings);

  const hasRequiredFieldErrors = extractionResult.errors.some(
    (e) => e.code === "MISSING_REQUIRED_FIELD"
  );

  const transformedData = transformExtracted(
    extractionResult.extracted,
    parsedFile
  );

  return {
    fileName: file.name,
    fileFormat,
    documentType: config.documentType,
    version: versionResult.version,
    isValid: !hasRequiredFieldErrors,
    extractedData: extractionResult.extracted,
    transformedData,
    errors,
    warnings,
  };
}

function detectFileFormat(fileName: string): FileFormat | null {
  const ext =
    fileName.lastIndexOf(".") >= 0
      ? fileName.slice(fileName.lastIndexOf(".")).toLowerCase()
      : "";
  return FILE_EXTENSION_TO_FORMAT[ext] ?? null;
}

export type {
  ExtractFileDataInput,
  ExtractFileDataOutput,
  FileExtractionResult,
  MergedExtractionData,
  DocumentType,
  FileFormat,
  ExtractionError,
  ExtractionWarning,
} from "./types";
